// Single source of truth for app toasts.
//
// The app standardised on Sonner (one <Toaster /> mounted in App.tsx via
// components/ui/sonner.tsx). The old Radix `useToast` is now a thin shim over
// this module (see hooks/use-toast.ts) so nothing renders a second toast stack.
//
// Everything that used to `import { toast } from "sonner"` now imports from
// here instead. This module is API-compatible with sonner's `toast` but adds:
//
//   1. Global duplicate suppression — the same message (same kind + title +
//      description) fired again within DEDUPE_WINDOW_MS reuses the existing
//      toast's id, so a double-submit or a store-AND-component both reporting
//      the same outcome shows ONE toast, not a stack of identical ones.
//   2. A `toast.promise`-style `withLoading` helper so an action shows exactly
//      one loading toast that is then REPLACED in place by success/error using
//      the same id (never a second toast).
//
// An explicit `id` always bypasses dedupe — the caller is controlling the
// toast lifecycle itself (e.g. loading → success with the same id).
import type { ReactNode } from "react";
import { toast as sonnerToast, type ExternalToast } from "sonner";

type Message = ReactNode;
type ToastKind = "default" | "success" | "error" | "info" | "warning";

const DEDUPE_WINDOW_MS = 3500;

const recent = new Map<string, { id: string | number; at: number }>();

function keyFor(kind: ToastKind, message: Message, data?: ExternalToast): string | null {
  // Only dedupe plain string messages; rich ReactNode content is rare and not
  // safely comparable.
  if (typeof message !== "string") return null;
  const description = typeof data?.description === "string" ? data.description : "";
  return `${kind} ${message} ${description}`;
}

function pruneIfNeeded(now: number) {
  if (recent.size <= 100) return;
  recent.forEach((value, key) => {
    if (now - value.at > DEDUPE_WINDOW_MS) recent.delete(key);
  });
}

function emit(
  kind: ToastKind,
  fn: (message: Message, data?: ExternalToast) => string | number,
  message: Message,
  data?: ExternalToast,
): string | number {
  // Caller-controlled id (loading→result lifecycle): pass straight through.
  if (data?.id != null) return fn(message, data);

  const now = Date.now();
  const key = keyFor(kind, message, data);
  if (key) {
    const prev = recent.get(key);
    if (prev && now - prev.at < DEDUPE_WINDOW_MS) {
      // Reuse the same toast id so sonner refreshes the existing toast in
      // place instead of stacking an identical one.
      const id = fn(message, { ...data, id: prev.id });
      recent.set(key, { id, at: now });
      return id;
    }
    const id = fn(message, data);
    recent.set(key, { id, at: now });
    pruneIfNeeded(now);
    return id;
  }

  return fn(message, data);
}

type ToastFn = {
  (message: Message, data?: ExternalToast): string | number;
  success: (message: Message, data?: ExternalToast) => string | number;
  error: (message: Message, data?: ExternalToast) => string | number;
  info: (message: Message, data?: ExternalToast) => string | number;
  warning: (message: Message, data?: ExternalToast) => string | number;
  loading: typeof sonnerToast.loading;
  message: (message: Message, data?: ExternalToast) => string | number;
  dismiss: typeof sonnerToast.dismiss;
  promise: typeof sonnerToast.promise;
  custom: typeof sonnerToast.custom;
};

const toastImpl = ((message: Message, data?: ExternalToast) =>
  emit("default", sonnerToast, message, data)) as ToastFn;

toastImpl.success = (message, data) => emit("success", sonnerToast.success, message, data);
toastImpl.error = (message, data) => emit("error", sonnerToast.error, message, data);
toastImpl.info = (message, data) => emit("info", sonnerToast.info, message, data);
toastImpl.warning = (message, data) => emit("warning", sonnerToast.warning, message, data);
// loading is part of a controlled lifecycle (it always gets replaced) — never deduped.
toastImpl.loading = sonnerToast.loading;
toastImpl.message = (message, data) => emit("default", sonnerToast.message, message, data);
toastImpl.dismiss = sonnerToast.dismiss;
toastImpl.promise = sonnerToast.promise;
toastImpl.custom = sonnerToast.custom;

export const toast = toastImpl;

/**
 * Run an async action behind a single loading toast that is replaced in place
 * by a success or error toast (same id — never a second toast). Returns the
 * action's result, re-throwing on failure so callers can still branch.
 */
export async function withLoading<T>(
  action: Promise<T> | (() => Promise<T>),
  messages: {
    loading: string;
    success: string | ((value: T) => string);
    error: string | ((error: unknown) => string);
  },
): Promise<T> {
  const id = sonnerToast.loading(messages.loading);
  try {
    const result = await (typeof action === "function" ? action() : action);
    const success =
      typeof messages.success === "function" ? messages.success(result) : messages.success;
    sonnerToast.success(success, { id });
    return result;
  } catch (error) {
    const message =
      typeof messages.error === "function" ? messages.error(error) : messages.error;
    sonnerToast.error(message, { id });
    throw error;
  }
}

export type { ExternalToast };
